import type { MouseEvent, ReactElement, ReactNode } from "react";
import { Activity, useCallback, useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";

import useLatestValue from "app/custom-hooks/useLatestValue";
import { CategorizedFiltersGrid } from "app/pages/sales-intelligence/common-components/filters-panel/components/FiltersList/CategorizedFiltersGrid";
import PanelFooter from "app/pages/sales-intelligence/common-components/filters-panel/components/PanelFooter/PanelFooter";
import TogglePanelButton from "app/pages/sales-intelligence/common-components/filters-panel/components/TogglePanelButton/TogglePanelButton";
import { useGenieTransformOrigin } from "app/pages/sales-intelligence/common-components/filters-panel/hooks/useGenieTransformOrigin";
import { useOverlayAnimationGate } from "app/pages/sales-intelligence/common-components/filters-panel/hooks/useOverlayAnimationGate";
import type { IFilterPanelContentProps } from "app/pages/sales-intelligence/common-components/filters-panel/types/props";
import type { IPlainObject } from "app/pages/sales-intelligence/types/plainObject";

import FilterCardActions from "../../components/FilterCardActions/FilterCardActions";
import { FilterPanelItem } from "../../components/FilterPanelItem/FilterPanelItem";
import type { IFilterItemCardActionsProps } from "../../components/FilterPanelItem/types";
import { useFiltersConfigContext } from "../../context/FiltersConfigContext";
import { useCategorizedFiltersViewModel } from "../../hooks/useCategorizedFiltersViewModel";
import { useFilterCategoryPanelState } from "../../hooks/useFilterCategoryPanelState";
import { useFilterExpansion } from "../../hooks/useFilterExpansion";
import { useScrollToExpandedFilter } from "../../hooks/useScrollToExpandedFilter";
import { useFiltersManager } from "../../hooks/useFiltersManager";
import { usePinnedFilters } from "../../hooks/usePinnedFilters";
import { useVisibleFilters } from "../../hooks/useVisibleFilters";
import type { IFilterManagerModel } from "../../services/filters-manager/types";
import filtersService from "../../services/filters-service";
import advancedSearchFilteringTrackingService from "../../services/tracking";
import { setIsFiltersPanelExpanded } from "../../store/action-creators";
import { selectFilterMode, selectIsFiltersPanelExpanded } from "../../store/selectors";
import { filtersConfigStatusMap } from "../../types/filtersConfig";
import type { FilterCategory } from "../../types/category";
import type { FilterInstanceType, IFilterInstance } from "../../types/filterInstance";
import type { SearchFilterInstance } from "../../types/searchFilterInstance";
import { CategoryOverlayContainer } from "../CategoryOverlay/CategoryOverlayContainer";
import { FilterPanelContentContainer } from "../FilterPanelContent/FilterPanelContentContainer";
import FiltersListContainer from "../FiltersList/FiltersListContainer";
import { TabbedFilterPanelContainer } from "../TabbedFilterPanel/TabbedFilterPanelContainer";

import { StyledFilterPanelPositioner } from "./styles";

const FILTER_PANEL_WIDTH = 352;

type Props<T extends IPlainObject, I extends SearchFilterInstance<T> = SearchFilterInstance<T>> = Pick<
    IFilterPanelContentProps<T, I>,
    "filtersConfig" | "filtersValue" | "onFiltersUpdate" | "onClearAllFilters"
> & {
    isLoading?: boolean;
    withAiAgentFilterMode?: boolean;
    activeTab?: string | null;
    renderChatSlot?: (filtersManager: IFilterManagerModel<T>) => ReactNode;
    belowSearchSlot?: ReactNode;
    onClearFilter?: (key: keyof T) => void;
    onFilterUpdate?: (key: keyof T, value?: T[keyof T]) => void;
};

const FilterPanelContainer = <T extends IPlainObject, I extends SearchFilterInstance<T> = SearchFilterInstance<T>>({
    isLoading,
    filtersConfig,
    filtersValue,
    activeTab,
    withAiAgentFilterMode = false,
    renderChatSlot,
    belowSearchSlot,
    onClearFilter: onClearFilterCallback,
    onFiltersUpdate,
    onFilterUpdate: onFilterUpdateCallback,
    onClearAllFilters,
}: Props<T, I>): ReactElement => {
    const dispatch = useDispatch();

    const isFilterPanelExpanded = useSelector(selectIsFiltersPanelExpanded);
    const filterMode = useSelector(selectFilterMode);

    const { categoriesList, status: filtersConfigStatus } = useFiltersConfigContext();

    const {
        selectedFilterKeys,
        nonPinnableFilterKeys,
        isPinDisabled,
        checkIsFilterPinned,
        onPinSingleFilter,
        onPinChangedFilters,
    } = usePinnedFilters({ filtersConfig });

    const { isCategoryPanelExpanded, onToggleCategoryPanel, onCloseCategoryPanel, onOpenCategoryPanel } =
        useFilterCategoryPanelState();

    const { shouldRenderOverlay, isOpen, isFullyOpen, skipEntryAnimation, onAnimationEnd } = useOverlayAnimationGate({
        isExpanded: isCategoryPanelExpanded,
    });

    const { positionerRef, transformOrigin, captureOrigin } = useGenieTransformOrigin();

    const handleOpenCategoryPanel = useCallback(
        (category: FilterCategory, event?: MouseEvent<HTMLElement>) => {
            captureOrigin(event);
            onOpenCategoryPanel(category);
        },
        [captureOrigin, onOpenCategoryPanel],
    );

    // The footer toggle is only present while collapsed, so a click here always opens — capture the
    // origin unconditionally (no need to read the panel-open flag, keeping this callback stable).
    const handleToggleCategoryPanel = useCallback(
        (event?: MouseEvent<HTMLElement>) => {
            captureOrigin(event);
            onToggleCategoryPanel();
        },
        [captureOrigin, onToggleCategoryPanel],
    );

    const filtersValueRef = useLatestValue(filtersValue);

    const handleFiltersUpdate = useCallback(
        (filters: Partial<T>): void => {
            onPinChangedFilters(filtersValueRef.current, filters);
            onFiltersUpdate(filters);
        },
        [filtersValueRef, onFiltersUpdate, onPinChangedFilters],
    );

    const handleFilterPanelToggle = useCallback((isExpanded: boolean) => {
        if (isExpanded) {
            advancedSearchFilteringTrackingService.filtersPanel.trackFilterPanelExpand();
        } else {
            advancedSearchFilteringTrackingService.filtersPanel.trackFilterPanelCollapsed();
        }

        dispatch(setIsFiltersPanelExpanded(isExpanded));
    }, []);

    // Declared before the visibility hooks: filters holding a value stay visible while collapsed, so the
    // manager (which owns dirtiness over the grouped key space) has to exist first.
    const filtersManager = useFiltersManager<T>({
        filtersValue,
        filtersConfig: filtersConfig as unknown as readonly IFilterInstance<T>[],
        onFiltersUpdate: handleFiltersUpdate,
    });

    const dirtyFilterKeys = useMemo(() => new Set(filtersManager.getDirtyFilters().map(String)), [filtersManager]);

    const visibleFiltersModel = useVisibleFilters({
        filtersConfig,
        selectedFilterKeys,
        dirtyFilterKeys,
    });

    const deferCollapseWhileSettling = Boolean(isLoading) || filtersConfigStatus !== filtersConfigStatusMap.ready;

    const { expandedFilterKey, onFilterExpand } = useFilterExpansion({
        ...visibleFiltersModel,
        activeTab,
        deferCollapseWhileSettling,
    });

    useScrollToExpandedFilter(isLoading);

    const collapsedFilterList = useMemo(
        () =>
            filtersService.getVisibleFilters(
                {
                    filtersConfig: filtersConfig as unknown as readonly SearchFilterInstance[],
                    selectedFilterKeys,
                    dirtyFilterKeys,
                    isSearchActive: visibleFiltersModel.isSearchActive,
                    searchMatchedFilters: visibleFiltersModel.searchMatchedFilters,
                },
                { isCategoryPanelExpanded: false, selectedCategory: null },
            ) as unknown as readonly IFilterInstance<T>[],
        [
            filtersConfig,
            selectedFilterKeys,
            dirtyFilterKeys,
            visibleFiltersModel.isSearchActive,
            visibleFiltersModel.searchMatchedFilters,
        ],
    );

    const { permanentFilters: collapsedPermanentFilters, groups: collapsedGroups } = useCategorizedFiltersViewModel({
        visibleFilters: collapsedFilterList,
        fullFiltersConfig: filtersConfig as unknown as readonly IFilterInstance<T>[],
        categories: categoriesList,
        categoriesWithHiddenFilters: visibleFiltersModel.categoriesWithHiddenFilters,
        isSearchActive: visibleFiltersModel.isSearchActive,
    });

    const {
        permanentFilters: overlayPermanentFilters,
        groups: overlayGroups,
        categoriesWithCounts,
    } = useCategorizedFiltersViewModel({
        visibleFilters: visibleFiltersModel.filterList as unknown as readonly IFilterInstance<T>[],
        fullFiltersConfig: filtersConfig as unknown as readonly IFilterInstance<T>[],
        categories: categoriesList,
        categoriesWithHiddenFilters: visibleFiltersModel.categoriesWithHiddenFilters,
        isSearchActive: visibleFiltersModel.isSearchActive,
    });

    const filtersManagerRef = useLatestValue(filtersManager);
    const onClearFilterCallbackRef = useLatestValue(onClearFilterCallback);
    const onFilterUpdateCallbackRef = useLatestValue(onFilterUpdateCallback);
    const expandedFilterKeyRef = useLatestValue(expandedFilterKey);
    const onFilterExpandRef = useLatestValue(onFilterExpand);

    const handleToggleExpand = useCallback(
        (filterName: keyof T) => {
            onFilterExpandRef.current(String(filterName) === expandedFilterKeyRef.current ? null : String(filterName));
        },
        [expandedFilterKeyRef, onFilterExpandRef],
    );

    const handleClearFilter = useCallback(
        (key: keyof T) => {
            filtersManagerRef.current.resetFilter(key);
            onClearFilterCallbackRef.current?.(key);
        },
        [filtersManagerRef, onClearFilterCallbackRef],
    );

    const handleFilterUpdate = useCallback(
        (key: keyof T, value?: T[keyof T]) => {
            filtersManagerRef.current.updateFilter(key, value);
            onFilterUpdateCallbackRef.current?.(key, value);
        },
        [filtersManagerRef, onFilterUpdateCallbackRef],
    );

    const handleCloseCategoryPanel = useCallback(() => {
        onCloseCategoryPanel();
    }, [onCloseCategoryPanel]);

    const renderCollapsedActions = useCallback(
        (props: IFilterItemCardActionsProps<T>) => (
            <FilterCardActions
                isExpanded={props.isExpanded}
                onToggleExpand={props.onToggleExpand}
                isCardExpandable={props.isCardExpandable}
                isShowClearButton={props.isShowClearButton}
                onClear={props.onClear}
                isShowPinButton={false}
                isPinDisabled={isPinDisabled}
                isPinned={checkIsFilterPinned(props.key as string)}
                onPinClick={() => onPinSingleFilter(props.key as string)}
            />
        ),
        [checkIsFilterPinned, isPinDisabled, onPinSingleFilter],
    );

    const renderOverlayActions = useCallback(
        (props: IFilterItemCardActionsProps<T>) => (
            <FilterCardActions
                isExpanded={props.isExpanded}
                onToggleExpand={props.onToggleExpand}
                isCardExpandable={props.isCardExpandable}
                isShowClearButton={props.isShowClearButton}
                onClear={props.onClear}
                isShowPinButton={!nonPinnableFilterKeys.has(props.key as string)}
                isPinDisabled={isPinDisabled}
                isPinned={checkIsFilterPinned(props.key as string)}
                onPinClick={() => onPinSingleFilter(props.key as string)}
            />
        ),
        [checkIsFilterPinned, isPinDisabled, onPinSingleFilter, nonPinnableFilterKeys],
    );

    const createFilterCardRenderer = useCallback(
        (cardRenderActions: (props: IFilterItemCardActionsProps<T>) => ReactElement) =>
            function renderFilterCard(config: IFilterInstance<T>) {
                return (
                    <FilterPanelItem<T>
                        key={String(config.key)}
                        isExpanded={expandedFilterKey === config.key}
                        isShowClearButton={filtersManager.isFilterDirty(config.key)}
                        isDisabled={filtersManager.isFilterDisabled(config.key)}
                        // IFilterInstance<T> distributes over keyof T (including symbol variants) while
                        // FilterInstanceType<T> uses the full keyof T union; the cast is safe at runtime.
                        filterConfig={config as unknown as FilterInstanceType<T, unknown, unknown>}
                        filterDtoValue={filtersManager.getFilterDtoValue(config.key)}
                        renderActions={cardRenderActions}
                        onToggleExpand={handleToggleExpand}
                        onFilterUpdate={handleFilterUpdate}
                        onClear={handleClearFilter}
                    />
                );
            },
        [expandedFilterKey, filtersManager, handleFilterUpdate, handleToggleExpand, handleClearFilter],
    );

    const renderCollapsedFilterCard = useMemo(
        () => createFilterCardRenderer(renderCollapsedActions),
        [createFilterCardRenderer, renderCollapsedActions],
    );

    const renderOverlayFilterCard = useMemo(
        () => createFilterCardRenderer(renderOverlayActions),
        [createFilterCardRenderer, renderOverlayActions],
    );

    const footerSlot = useMemo(
        () => (
            <PanelFooter>
                <TogglePanelButton onClick={handleToggleCategoryPanel} />
            </PanelFooter>
        ),
        [handleToggleCategoryPanel],
    );

    const filtersListContainer = (
        <Activity mode={isFullyOpen ? "hidden" : "visible"}>
            <FiltersListContainer
                filtersManager={filtersManager}
                permanentFilters={collapsedPermanentFilters}
                groups={collapsedGroups}
                renderFilterCard={renderCollapsedFilterCard}
                isLoading={isLoading}
                withSearch
                belowSearchSlot={belowSearchSlot}
                footerSlot={footerSlot}
                onOpenCategoryPanel={handleOpenCategoryPanel}
            />
        </Activity>
    );

    return (
        <StyledFilterPanelPositioner ref={positionerRef}>
            <FilterPanelContentContainer<T>
                isExpanded={isFilterPanelExpanded}
                filtersManager={filtersManager}
                width={FILTER_PANEL_WIDTH}
                filterMode={filterMode}
                withAiAgentFilterMode={withAiAgentFilterMode}
                onPanelToggle={handleFilterPanelToggle}
                onClearAllFilters={onClearAllFilters}
            >
                {withAiAgentFilterMode && renderChatSlot ? (
                    <TabbedFilterPanelContainer
                        listSlot={filtersListContainer}
                        chatSlot={renderChatSlot(filtersManager)}
                    />
                ) : (
                    filtersListContainer
                )}
            </FilterPanelContentContainer>

            {shouldRenderOverlay ? (
                <CategoryOverlayContainer
                    filtersManager={filtersManager}
                    categoriesWithCounts={categoriesWithCounts}
                    isOpen={isOpen}
                    skipEntryAnimation={skipEntryAnimation}
                    transformOrigin={transformOrigin}
                    isLoading={isLoading}
                    onClose={handleCloseCategoryPanel}
                    onAnimationEnd={onAnimationEnd}
                    belowSearchSlot={belowSearchSlot}
                >
                    <CategorizedFiltersGrid
                        permanentFilters={overlayPermanentFilters}
                        groups={overlayGroups}
                        renderFilterCard={renderOverlayFilterCard}
                    />
                </CategoryOverlayContainer>
            ) : null}
        </StyledFilterPanelPositioner>
    );
};

export default FilterPanelContainer;
